"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { eventName, totalPieces } from "@/lib/avatar/events";
import { phaseLabel } from "./eventTime";

// La fenêtre de l'événement n'est téléchargée que si le membre l'ouvre.
const EventModal = dynamic(() => import("./EventModal"), { ssr: false });

// En tête des quêtes du jour pendant un événement de puzzles : un bandeau aux couleurs de
// l'événement, qui dit ce que les coffres contiennent en plus et où en est le membre. C'est
// ici qu'on comprend que chaque coffre donne une pièce. Un clic ouvre les puzzles.
export default function EventQuestsHeader({ state, className = "" }) {
    const t = useTranslations();
    const locale = useLocale();
    const [open, setOpen] = useState(false);
    const event = state?.event;
    if (!event || event.phase !== "open") return null;

    const theme = event.theme || {};
    const total = totalPieces(event);
    const count = state.mine?.total || 0;
    const [label, values] = phaseLabel(event, locale);

    return (
        <>
            {/* Une grille qui suit la largeur de la colonne : dans la colonne d'une formation, le
                bouton passe sous le texte et la barre prend toute la largeur ; à partir de 448 px,
                tout tient sur une ligne. */}
            <div className={`@container ${className}`}>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="group relative grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-3 overflow-hidden rounded-xl p-3.5 text-left text-white transition-transform duration-200 hover:scale-[1.01] @md:grid-cols-[auto_minmax(0,1fr)_auto] @md:gap-y-1.5 @md:p-3"
                    style={{ background: `linear-gradient(120deg, ${theme.dark || "#2A1F4D"}, ${theme.dark || "#2A1F4D"} 45%, ${theme.color || "#613BDB"})` }}
                >
                    {theme.chests?.common && <Image src={theme.chests.common} alt="" width={52} height={45} unoptimized className="col-start-1 row-start-1 h-[45px] w-[52px] drop-shadow-lg transition-transform duration-300 group-hover:-rotate-6 @md:row-span-2" />}
                    <span className="col-start-2 row-start-1 min-w-0 @md:self-end">
                        <span className="block text-sm font-bold leading-snug">{eventName(event, locale)}</span>
                        <span className="block text-xs text-white/70">{t(label, values)}</span>
                    </span>
                    <span className="col-span-2 row-start-2 min-w-0 @md:col-span-1 @md:col-start-2 @md:self-start">
                        <span className="block text-xs text-white/85">{t(total > 0 && count >= total ? "avatar_event_quests_done" : "avatar_event_quests_hint")}</span>
                        <span className="mt-2 flex items-center gap-2 @md:mt-1.5">
                            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/20">
                                <span className="block h-full rounded-full bg-white transition-[width] duration-500 ease-out" style={{ width: `${total ? Math.round((count / total) * 100) : 0}%` }} />
                            </span>
                            <span className="shrink-0 text-[11px] font-medium tabular-nums text-white/85">{t("avatar_event_quests_progress", { count, total })}</span>
                        </span>
                    </span>
                    <span className="col-span-2 row-start-3 rounded-lg bg-white/15 px-2.5 py-2 text-center text-xs font-semibold transition-colors group-hover:bg-white/25 @md:col-span-1 @md:col-start-3 @md:row-span-2 @md:row-start-1 @md:py-1.5">{t("avatar_event_quests_cta")}</span>
                </button>
            </div>
            {open && <EventModal isOpen onClose={() => setOpen(false)} />}
        </>
    );
}
